import { type ApiContext, DEFAULT_HOST, type IntentPayload, postIntent } from './api'
import { AvailabilityLookup } from './availability'
import { type ClientData, type LoadContext, loadClientData } from './data'
import { emit, EVENT_SIGNUP_SUBMITTED } from './events'
import { DEFAULT_STOREFRONT_API_VERSION, deriveNamespace } from './metafields'
import {
  openInlineForm as mountInlineForm,
  openModal as mountModal,
  removeInlineForm as unmountInlineForm,
} from './modal'
import type { InlineFormOptions, ModalContext, ModalHandle, ModalOptions } from './modal'
import {
  buildButtonContent,
  buildPreorderLine,
  openPreorderModal as mountPreorderModal,
  resolvePaymentOptions,
} from './preorder'
import type {
  PreorderConfirmation,
  PreorderContext,
  PreorderButtonContent,
  PreorderModalOptions,
  PreorderPaymentOption,
} from './preorder'
import type {
  CartLine,
  SellingPlan,
  SignupInput,
  SignupResult,
  StoqConfig,
  StoqSettings,
  VariantState,
} from './types'
import { compact, normalizeLocale, toNumericId, warn } from './util'
import {
  availabilityOf,
  isInStock,
  isOutOfStockPurchasable,
  variantIdOf,
  type VariantInput,
} from './variant-input'
import {
  renderNotifyMe as mountNotifyMe,
  renderPreorderBadge as mountPreorderBadge,
  renderPreorderButton as mountPreorderButton,
  renderPreorderWidget as mountPreorderWidget,
} from './widgets'
import type {
  NotifyMeWidgetOptions,
  PreorderBadgeWidgetOptions,
  PreorderButtonWidgetOptions,
  PreorderWidgetOptions,
  WidgetHandle,
} from './widgets'

const SIGNUP_SOURCE = 'storefront-sdk'

function buildContext(config: StoqConfig): { api: ApiContext; load: LoadContext } {
  if (!config || typeof config.shop !== 'string' || config.shop.trim() === '') {
    throw new TypeError('[Stoq] init requires `shop` (e.g. "my-store.myshopify.com")')
  }
  if (typeof config.storefrontToken !== 'string' || config.storefrontToken.trim() === '') {
    throw new TypeError(
      '[Stoq] init requires `storefrontToken` — the shop\'s PUBLIC Storefront API access '
        + 'token (in Hydrogen: env.PUBLIC_STOREFRONT_API_TOKEN). The SDK reads all data from '
        + 'the shop\'s own Storefront API endpoint with it.',
    )
  }

  const host = (config.host ?? DEFAULT_HOST).replace(/\/+$/, '')
  const shop = config.shop.trim()
  const marketId = config.market !== undefined && config.market !== null && config.market !== ''
    ? toNumericId(config.market)
    : undefined
  const locale = normalizeLocale(config.locale)

  return {
    api: { host, shop, marketId, locale },
    load: {
      shop,
      marketId,
      locale,
      storefront: {
        shop,
        token: config.storefrontToken.trim(),
        apiVersion: config.storefrontApiVersion ?? DEFAULT_STOREFRONT_API_VERSION,
        namespace: config.namespace ?? deriveNamespace(host),
        marketId,
      },
    },
  }
}

export class StoqClient {
  private readonly ctx: ApiContext
  private readonly loadCtx: LoadContext
  private readonly availability: AvailabilityLookup
  private data: ClientData

  private constructor(ctx: ApiContext, loadCtx: LoadContext, data: ClientData, country?: string) {
    this.ctx = ctx
    this.loadCtx = loadCtx
    this.data = data
    this.availability = new AvailabilityLookup(loadCtx.storefront, country)
  }

  /** @internal Use {@link init} instead. */
  static async create(config: StoqConfig): Promise<StoqClient> {
    const { api, load } = buildContext(config)
    const data = await loadClientData(load, false)
    return new StoqClient(api, load, data, config.country)
  }

  /** The shop's myshopify domain this client is bound to. */
  get shop(): string {
    return this.ctx.shop
  }

  /** Parsed shop settings (frozen). Prefer the named getters below for the common flags. */
  get settings(): Readonly<StoqSettings> {
    return this.data.settings
  }

  /** Whether the merchant has preorders enabled (shop-level). */
  get preorderEnabled(): boolean {
    return this.data.settings.preorder_enabled === true
  }

  /** Whether back-in-stock signups (the notify-me widget) are enabled (shop-level). */
  get signupsEnabled(): boolean {
    return this.data.settings.enable_signup_widget === true
  }

  /** The shop's selling plans (frozen). */
  get sellingPlans(): ReadonlyArray<SellingPlan> {
    return this.data.sellingPlans
  }

  /** Busts the caches and refetches all datasets (incl. variant availability). */
  async refresh(): Promise<void> {
    this.availability.clear()
    this.data = await loadClientData(this.loadCtx, true)
  }

  /**
   * The selling plan that applies to a variant in the current market.
   * Market-scoped plans win over global plans (mirrors the embed's
   * getSellingPlan).
   */
  sellingPlanFor(variant: VariantInput): SellingPlan | null {
    const id = variantIdOf(variant)
    const plans = this.data.sellingPlans
    const marketId = this.ctx.marketId

    if (marketId !== undefined) {
      const marketPlan = plans.find((plan) =>
        plan.enabled
        && plan.markets_enabled === true
        && Array.isArray(plan.shopify_market_ids)
        && plan.shopify_market_ids.includes(marketId)
        && plan.variant_ids.includes(id),
      )
      if (marketPlan) return marketPlan
    }

    return (
      plans.find((plan) => plan.enabled && !plan.markets_enabled && plan.variant_ids.includes(id))
      ?? null
    )
  }

  /**
   * The availability-independent slice of {@link VariantState}: plan id,
   * shipping text, limits. `isPreorder` is filled in by the caller.
   */
  private baseState(id: number, plan: SellingPlan | null): Omit<VariantState, 'isPreorder'> {
    const { preorderLimits, shippingTexts } = this.data

    const planId = plan
      ? ((plan.default_shopify_selling_plan_id ?? plan.shopify_selling_plan_id) ?? null)
      : null

    const limitEntry = preorderLimits[String(id)]
    const soldCount = limitEntry ? Number(limitEntry[0]) || 0 : 0
    // A limit of 0 means "no limit configured" (the embed treats falsy limits
    // as unlimited).
    const rawMax = limitEntry ? Number(limitEntry[1]) : 0
    const maxCount = rawMax > 0 ? rawMax : null
    const remainingCount = maxCount !== null ? Math.max(0, maxCount - soldCount) : null

    return {
      sellingPlanId: planId,
      sellingPlanGid: planId !== null ? `gid://shopify/SellingPlan/${planId}` : null,
      shippingText: shippingTexts[String(id)] || null,
      maxCount,
      remainingCount,
    }
  }

  /** Whether the plan is currently sellable given availability (`undefined` = can't tell). */
  private sellableUnder(
    plan: SellingPlan,
    availability: ReturnType<typeof availabilityOf>,
  ): boolean | undefined {
    return plan.inventory_provider === 'shopify'
      ? isInStock(availability)
      : isOutOfStockPurchasable(availability)
  }

  /**
   * Sync state for the preorder-content paths (button copy, modal): exact
   * when the input carries availability fields, conservative (`isPreorder:
   * false`) otherwise — those paths only consume the availability-independent
   * fields, so no lookup is warranted.
   */
  private syncState(variant: VariantInput): VariantState {
    const id = variantIdOf(variant)
    const plan = this.sellingPlanFor(id)
    const sellable = plan !== null && this.data.settings.preorder_enabled === true
      ? (this.sellableUnder(plan, availabilityOf(variant)) ?? false)
      : false
    return { isPreorder: sellable, ...this.baseState(id, plan) }
  }

  /**
   * Derived state for a variant. Pass a variant OBJECT with availability
   * fields (e.g. `{ id, availableForSale, currentlyNotInStock }` straight
   * from your product query) and this resolves immediately with no network.
   * With a bare id, the SDK looks the availability up itself from the shop's
   * Storefront API (batched — concurrent lookups share one request — and
   * cached for 60 seconds).
   *
   * Availability is what decides whether an attached plan is sellable:
   *
   * - STOQ-inventory plans sell out-of-stock-but-purchasable units
   *   (`currentlyNotInStock: true`).
   * - Shopify-inventory plans sell in-stock units
   *   (`availableForSale && !currentlyNotInStock`).
   *
   * Note: the SDK's own lookup answers for the shop's default country unless
   * `country` is configured — pass your own `@inContext`-queried variant
   * object if you localize inventory by market.
   */
  async getVariantState(variant: VariantInput): Promise<VariantState> {
    const id = variantIdOf(variant)
    const plan = this.sellingPlanFor(id)
    const base = this.baseState(id, plan)

    if (plan === null || this.data.settings.preorder_enabled !== true) {
      return { isPreorder: false, ...base }
    }

    let sellable = this.sellableUnder(plan, availabilityOf(variant))
    if (sellable === undefined) {
      sellable = this.sellableUnder(plan, await this.availability.get(id)) ?? false
    }

    return { isPreorder: sellable, ...base }
  }

  /**
   * Builds a Storefront Cart API line for a variant. When the variant should
   * be purchased as a preorder (resolved like {@link getVariantState} — no
   * network when the variant object carries availability fields) the line
   * carries the preorder marker for the offer's kind:
   *
   * - Shopify-selling-plan offers → `sellingPlanId` (`gid://shopify/SellingPlan/<id>`).
   * - Property-based offers (`use_shopify_selling_plan: false`) → `attributes`
   *   (`_stoq_shopify_selling_plan_id` + `Purchase type` line-item
   *   properties, the same marker the theme app embed writes) and NO
   *   `sellingPlanId`.
   *
   * Pass the whole line to `cartLinesAdd` / Hydrogen's `CartForm` — it is
   * already `CartLineInput`-shaped.
   */
  async cartLineFor(variant: VariantInput, quantity = 1): Promise<CartLine> {
    const id = variantIdOf(variant)
    const state = await this.getVariantState(variant)
    const plan = state.isPreorder ? this.sellingPlanFor(id) : null
    if (plan === null) {
      return { merchandiseId: `gid://shopify/ProductVariant/${id}`, quantity }
    }
    return buildPreorderLine(plan, id, quantity, state.sellingPlanId)
  }

  /**
   * Creates a back-in-stock signup (intent). This is the SDK's only call to
   * the STOQ API. Fires the documented `stoq:restock-modal:submitted` event
   * on success.
   */
  async createSignup(input: SignupInput): Promise<SignupResult> {
    if (!input || (input.email === undefined && input.phone === undefined)) {
      throw new TypeError('[Stoq] createSignup requires `email` or `phone`')
    }
    if (input.productId === undefined || input.productId === null) {
      // The backend requires both ids; failing early beats a 200-with-errors.
      throw new TypeError('[Stoq] createSignup requires `productId` (the backend rejects signups without it)')
    }

    const variantId = variantIdOf(input.variantId)
    const productId = toNumericId(input.productId)

    const payload: IntentPayload = {
      intent: compact({
        shopify_variant_id: variantId,
        shopify_product_id: productId,
        shopify_market_id: this.ctx.marketId,
        country: input.country,
        channel: input.channel,
        quantity: input.quantity ?? 1,
        source: SIGNUP_SOURCE,
      }),
      customer: compact({
        email: input.email,
        phone: input.phone,
        name: input.name,
        locale: input.locale ?? this.ctx.locale,
        accepts_marketing: input.acceptsMarketing,
        country: input.country,
        country_code: input.countryCode,
        shopify_market_id: this.ctx.marketId,
        shopify_customer_id: input.shopifyCustomerId !== undefined && input.shopifyCustomerId !== null
          ? toNumericId(input.shopifyCustomerId)
          : undefined,
      }),
    }

    if (input.product) {
      const product = compact({
        title: input.product.title,
        variant_title: input.product.variantTitle,
        vendor: input.product.vendor,
        sku: input.product.sku,
        variant_count: input.product.variantCount,
      })
      if (Object.keys(product).length > 0) payload.product = product
    }

    const { status, body } = await postIntent(this.ctx, payload)
    const intents = Array.isArray(body?.intents) ? body.intents : []
    const ok = status === 201

    if (ok) {
      emit(EVENT_SIGNUP_SUBMITTED, {
        action: 'submitted',
        product: { id: productId ?? null },
        variant: { id: variantId },
        customer: { email: input.email ?? null, phone: input.phone ?? null },
      })
    }

    return { ok, status, intents, errors: body?.errors ?? [] }
  }

  /**
   * Merchant-configured preorder CTA copy for a variant — `label`
   * (`preorder_button_text`), `disclaimer` (HTML
   * `preorder_button_description`, when enabled) and `shippingText` (the
   * plan's `preorder_shipping_text` template with its date substituted).
   * Returns null when no enabled plan applies to the variant in the current
   * market. Use it to render your own preorder button with the copy the
   * merchant configured in STOQ.
   */
  preorderButtonFor(variant: VariantInput): PreorderButtonContent | null {
    const plan = this.sellingPlanFor(variant)
    if (plan === null) return null
    return buildButtonContent(plan, this.syncState(variant), this.settings, this.ctx.locale)
  }

  /**
   * The variant's payment options (full vs partial payment), translated and
   * sorted default-first. Empty when no enabled plan applies. Single-option
   * plans return one entry (the plan's own billing config).
   */
  preorderPaymentOptionsFor(variant: VariantInput): PreorderPaymentOption[] {
    const plan = this.sellingPlanFor(variant)
    if (plan === null) return []
    return resolvePaymentOptions(plan, this.settings, this.ctx.locale)
  }

  /**
   * Like {@link cartLineFor}, but for an explicitly chosen payment option:
   * the line carries the marker for the given option (payment options carry
   * their own Shopify selling plan ids). Pass `sellingPlanId` from a
   * {@link PreorderPaymentOption}; omit it to use the plan's default.
   * Returns null when no enabled plan applies. Property-based plans
   * (`use_shopify_selling_plan: false`) get line-item `attributes` instead of
   * a `sellingPlanId`.
   *
   * Stock gate: this method is synchronous, so it can only check stock when
   * you pass a variant OBJECT with availability fields — then it returns
   * `null` for a variant that isn't currently preorderable. With a bare id
   * it cannot look availability up and returns the line regardless; prefer
   * {@link cartLineFor} (or call {@link getVariantState} first) when all you
   * hold is an id.
   */
  preorderLineFor(
    variant: VariantInput,
    quantity = 1,
    sellingPlanId?: number | null,
  ): CartLine | null {
    const id = variantIdOf(variant)
    const plan = this.sellingPlanFor(id)
    if (plan === null) return null
    if (this.sellableUnder(plan, availabilityOf(variant)) === false) return null
    const chosen = sellingPlanId
      ?? this.preorderPaymentOptionsFor(id)[0]?.sellingPlanId
      ?? this.baseState(id, plan).sellingPlanId
    return buildPreorderLine(plan, id, quantity, chosen)
  }

  private preorderContext(variant: VariantInput): PreorderContext | null {
    const id = variantIdOf(variant)
    const plan = this.sellingPlanFor(id)
    if (plan === null) return null
    return {
      settings: this.settings,
      locale: this.ctx.locale,
      plan,
      // The modal only consumes availability-independent state fields.
      state: this.syncState(variant),
      variantId: id,
    }
  }

  /**
   * Opens the preorder modal (payment-option picker + quantity input) for a
   * variant. Resolves with a {@link PreorderConfirmation} — including a
   * ready-to-use `cartLine` honoring the chosen option's selling plan id —
   * when the shopper confirms, or `null` when they dismiss (or when no
   * enabled plan applies to the variant). `onConfirm` is invoked as well.
   *
   * Fast path: when the plan has a single payment option, no acknowledgement
   * requirement and the requested quantity is within the preorder limits, no
   * UI is needed — the promise resolves immediately without rendering (and
   * without modal events), mirroring the theme embed. Fires
   * `stoq:preorder-modal:opened`/`closed` and
   * `stoq:preorder-payment-option:selected` when the modal does render.
   * The rendering path is browser-only — it rejects during SSR.
   */
  openPreorderModal(options: PreorderModalOptions): Promise<PreorderConfirmation | null> {
    const ctx = this.preorderContext(options.variantId)
    if (ctx === null) {
      warn(`openPreorderModal: no enabled selling plan applies to variant ${variantIdOf(options.variantId)}`)
      return Promise.resolve(null)
    }

    // Stock gate — the same rule cartLineFor/getVariantState apply. Without
    // it this handed back a line with a selling plan attached to a unit that
    // isn't currently sellable as a preorder (e.g. back in stock under a
    // STOQ-inventory offer).
    const decide = (isPreorder: boolean): Promise<PreorderConfirmation | null> => {
      if (!isPreorder) {
        warn(`openPreorderModal: variant ${ctx.variantId} is not currently preorderable (stock state) — resolving null`)
        return Promise.resolve(null)
      }
      return mountPreorderModal(ctx, { ...options, variantId: ctx.variantId })
    }

    // With a variant object that carries availability the answer is known
    // synchronously, so the modal still mounts in the same tick as the click
    // (user activation / focus). Only a bare id needs the (batched, cached)
    // availability lookup first.
    const knownSync = this.sellableUnder(ctx.plan, availabilityOf(options.variantId)) !== undefined
    if (knownSync) return decide(ctx.state.isPreorder)
    return this.getVariantState(options.variantId).then((state) => decide(state.isPreorder))
  }

  private modalContext(): ModalContext {
    return {
      settings: this.settings,
      createSignup: (input) => this.createSignup(input),
    }
  }

  /**
   * Opens the back-in-stock notify-me modal for a variant. Texts and colors
   * default to the shop's settings; pass `texts`/`styles` to override per
   * call. Fires `stoq:restock-modal:opened`/`closed` and (on successful
   * signup) `stoq:restock-modal:submitted`. Browser-only — throws during SSR.
   */
  openModal(options: ModalOptions): ModalHandle {
    return mountModal(this.modalContext(), options)
  }

  /**
   * Renders the same signup form into a merchant-supplied container element
   * (or CSS selector). Returns null when the container is not found.
   */
  openInlineForm(options: InlineFormOptions): ModalHandle | null {
    return mountInlineForm(this.modalContext(), options)
  }

  /**
   * Removes inline forms created by {@link openInlineForm}. With no argument
   * all inline forms are removed; pass the container (element or selector)
   * to remove a specific one.
   */
  removeInlineForm(container?: Element | string): void {
    unmountInlineForm(container)
  }

  /**
   * Renders the preorder CTA button into a container you point at and wires
   * the click → payment-option modal → cart flow. The imperative twin of the
   * React `<StoqPreorderButton>`, for head-embed / vanilla storefronts. Shows
   * the button only when the variant is currently purchasable as a preorder.
   * Returns a {@link WidgetHandle} (re-render with `update(variant)`, remove
   * with `destroy()`). Browser-only.
   */
  renderPreorderButton(
    variant: VariantInput,
    options: PreorderButtonWidgetOptions,
  ): Promise<WidgetHandle> {
    return mountPreorderButton(this, variant, options)
  }

  /**
   * Renders a `Preorder` badge into a container, shown only while the variant
   * is purchasable as a preorder. Imperative twin of `<StoqPreorderBadge>`.
   */
  renderPreorderBadge(
    variant: VariantInput,
    options: PreorderBadgeWidgetOptions,
  ): Promise<WidgetHandle> {
    return mountPreorderBadge(this, variant, options)
  }

  /**
   * Renders the back-in-stock notify-me widget (a button that expands into
   * the signup form) into a container. Imperative twin of
   * `<StoqNotifyMeButton>`. Renders nothing when signups are disabled.
   */
  renderNotifyMe(variant: VariantInput, options: NotifyMeWidgetOptions): Promise<WidgetHandle> {
    return mountNotifyMe(this, variant, options)
  }

  /**
   * One-call preorder widget: renders the preorder badge (optional) and CTA
   * button for a variant, with an optional notify-me fallback when the variant
   * isn't preorder-eligible. The drop-in "do everything" entry point.
   */
  renderPreorderWidget(
    variant: VariantInput,
    options: PreorderWidgetOptions,
  ): Promise<WidgetHandle> {
    return mountPreorderWidget(this, variant, options)
  }
}
